<script setup lang="ts">
import { ChevronUpIcon } from '@blpde/nx-icons';
import { ref } from 'vue';
import { NxButton } from '../Button';
import { NxDropdown } from '../Dropdown';
import { NxUtilityButtonProps, NxUtilityDropdownElement } from './internal';

const props = withDefaults(defineProps<NxUtilityButtonProps>(), { label: 'Aktionen' });

const isOpen = ref<boolean>(false);

function onSelectDropdownElement(element: NxUtilityDropdownElement) {
  isOpen.value = false;
  element.onSelect();
}
</script>

<template>
  <div class="select-wrapper" @mouseleave="isOpen = false" @mouseenter="isOpen = true">
    <NxDropdown :is-open="isOpen" @close="isOpen = false">
      <template #trigger>
        <NxButton class="utility-button" variant="outline" width="narrow" @click="isOpen = !isOpen">
          <span class="button-label">
            <slot>{{ props.label }}</slot>
          </span>
          <ChevronUpIcon class="chevron-icon" :class="{ down: !isOpen }" :filled="true" />
        </NxButton>
      </template>
      <div class="actions-dropdown">
        <NxButton
          v-for="(element, index) in props.dropdownElements"
          :key="index"
          class="popper-element"
          :class="{ reversed: element.icon?.align === 'end', 'divider-top': element.divider }"
          variant="outline"
          width="full"
          @click="onSelectDropdownElement(element)"
        >
          <component
            :is="element.icon.component"
            v-if="element.icon"
            class="icon-fixed nxt-icon-md"
            :filled="element.icon.filled"
            :color="element.icon.color"
            :class="{
              start: element.icon?.align !== 'start',
              end: element.icon?.align === 'end',
            }"
          />
          <span class="dropdown-element-label">{{ element.label }}</span>
        </NxButton>
      </div>
    </NxDropdown>
  </div>
</template>

<style lang="css" scoped>
.chevron-icon {
  transition: transform var(--nxt-motion-fast) ease;
  transform-origin: center;
}

.chevron-icon.down {
  transform: rotate(-180deg);
}

.reversed {
  flex-direction: row-reverse;
}

.button-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: var(--nxt-font);
  font-weight: var(--nxt-font-weight-medium);
}

.icon-fixed {
  flex-shrink: 0;
  display: flex;
}

.icon-fixed.start {
  margin-right: var(--nxt-space-xs);
}

.icon-fixed.end {
  margin-left: var(--nxt-space-xs);
}

.dropdown-element-label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  font-size: var(--nxt-font-size-sm);
  font-family: var(--nxt-font);
  font-weight: var(--nxt-font-weight-semibold);
}

/* Sized by its label (a flex column parent such as NxUtilityPanel still
   stretches it); it used to take the whole row even without a label. */
.select-wrapper {
  position: relative;
  display: inline-flex;
  min-width: calc(var(--nxt-size-unit) * 10);
  height: 2.8125em;
}

.utility-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
  width: 100%;
  flex: 1;
  height: 2.8125em !important;
  box-shadow: var(--nxt-shadow-sm);
}

.actions-dropdown {
  --border-color: var(--nxt-border);
  box-sizing: border-box;
  padding: var(--nxt-space-xs);
  background-color: var(--nxt-surface-raised-bg);
  border: var(--nxt-hairline) solid var(--border-color);
  border-radius: var(--nxt-radius-sm);
  box-shadow: var(--nxt-shadow-sm);
}

.popper-element {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  padding-left: var(--nxt-space-xs);
  padding-right: var(--nxt-space-xs);
  column-gap: 0em;
}

.divider-top {
  margin-top: var(--nxt-space-sm);
  position: relative;
  overflow: visible !important;
}

.divider-top::before {
  content: '';
  position: absolute;
  top: -0.375em;
  left: 0;
  width: 100%;
  border-top: var(--nxt-hairline) dashed var(--border-color);
  pointer-events: none;
}
</style>
